import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit, OnDestroy, ViewChild } from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { MatIconModule } from '@angular/material/icon';
import saveAs from 'file-saver';
import printJS from 'print-js';
import { MatSidenavModule, MatSidenav } from '@angular/material/sidenav';
import { ConfigurableFocusTrapFactory } from '@angular/cdk/a11y';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { DemandeRecrutementFormComponent } from './demande-recrutement-form/demande-recrutement-form.component';
import { DemandeRenouvellementFormComponent } from './demande-renouvellement-form/demande-renouvellement-form.component';
import { DemandeRecrutementFormValue, DemandeRenouvellementFormValue, DemandeRenouvellementPost, ModifyRecruitmentRequest } from '@models/api/requests';
import { DemandeType } from '@models/enums';
import { DemandeStatusService } from 'src/app/core/services/demande-status.service';
import { Subject, Observable } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { AgentsService } from 'src/app/core/services/agents.service';
import { DemandeRenouvellementService } from 'src/app/core/services/demande-renouvellement.service';
import { DemandeRecrutement } from '@models/domains/recruitment';
import { Renouvellement } from '@models/domains/renewal';
import { DemandeRecrutementService } from 'src/app/core/services/demande-recrutement.service';
import { ValidationDemandesService } from 'src/app/core/services/validation-demandes.service';
import { FileService } from 'src/app/core/services/file.service';
import { FormUtilsService } from 'src/app/core/services/form-utils.service';
import { ValidationModalComponent } from '../validation-modal/validation-modal.component';
import { RecallActionDialogComponent } from './recall-action-dialog/recall-action-dialog.component';
import { GetAdviceComponent } from './get-advice/get-advice.component';
import { DemandesService } from 'src/app/core/services/demandes.service';
import { AnswerDemandeAvisRequest, CreateDemandeAvisRequest } from '@models/api/requests';
import { DateTime } from 'luxon';
import { StatusAvis } from '@models/enums';
import { BaseUser } from '@models/shared';
import { Role } from '@models/enums/role.enum';
import { RoleService } from 'src/app/core/services/role.service';

import type { PdfViewerAction, PdfViewerData } from './pdf-viewer.types';

@Component({
  selector: 'app-pdf-viewer',
  imports: [
    MatIconModule,
    MatSlideToggleModule,
    MatButtonModule,
    MatDialogModule,
    PdfViewerModule,
    MatSidenavModule,
    MatExpansionModule,
    MatMenuModule,
    MatDividerModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    DemandeRecrutementFormComponent,
    DemandeRenouvellementFormComponent
  ],
  templateUrl: './pdf-viewer.component.html',
  styleUrls: ['./pdf-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: ConfigurableFocusTrapFactory }]
})
export class PdfViewerComponent implements OnInit, OnDestroy {
  pdfSource: string | undefined = undefined;
  currentPdfIndex: number = 0;
  pmdfZoomFactor = 1.1;
  isOpened = true;
  isReadOnly = false;
  demandeDetails: DemandeRecrutement | null = null;
  demandeRenouvellement: Renouvellement | null = null;
  demandeurAvis: string = '';
  currentDemandeType: DemandeType = DemandeType.Recrutement;
  currentDemande: DemandeRecrutement | Renouvellement | null = null;
  currentAgent$: Observable<BaseUser>;
  private needsRefresh = false;
  @ViewChild(DemandeRecrutementFormComponent) recrutementForm!: DemandeRecrutementFormComponent;
  @ViewChild(DemandeRenouvellementFormComponent) renouvellementForm!: DemandeRenouvellementFormComponent;
  protected DemandeType = DemandeType;
  private readonly destroy$ = new Subject<void>();
  currentStatus: string | undefined;
  private isAdmin = false;
  @ViewChild('sidenav') sidenav!: MatSidenav;
  @ViewChild('pdfViewer') pdfViewer: any; // ng2-pdf-viewer type
  private previousZoom = 1.1;
  public StatusAvis = StatusAvis;

  get isBulkMode(): boolean {
    return (this.data.demandes?.length || 0) > 1;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: PdfViewerData,
    private readonly dialog: MatDialogRef<PdfViewerComponent>,
    private readonly matDialog: MatDialog,
    private readonly fileService: FileService,
    private readonly validationDemandesService: ValidationDemandesService,
    private readonly demandeRecrutementService: DemandeRecrutementService,
    private readonly cdr: ChangeDetectorRef,
    public readonly demandeStatusService: DemandeStatusService,
    private readonly agentsService: AgentsService,
    private readonly demandeRenouvellementService: DemandeRenouvellementService,
    private readonly formUtilsService: FormUtilsService,
    private readonly demandesService: DemandesService,
    private readonly roleService: RoleService
  ) {
    this.currentAgent$ = this.agentsService.getCurrentAgent();
  }

  ngOnInit(): void {
    // Set read-only mode based on action type
    if (this.data.demandes?.length) {
      this.loadCurrentPdf();
      this.loadDemandeDetails();
    }

    this.roleService
      .getRole()
      .pipe(takeUntil(this.destroy$))
      .subscribe((roles) => {
        this.isAdmin = roles.includes(Role.PAMA_ADMIN);
        this.cdr.detectChanges();
      });
  }

  loadDemandeDetails(): void {
    if (!this.data.demandes?.length) return;
    const currentDemande = this.data.demandes[this.currentPdfIndex];
    this.currentDemandeType = currentDemande.type;
    if (currentDemande.type === DemandeType.Recrutement) {
      this.demandeRecrutementService
        .getDemandesRecrutementById(currentDemande.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response: DemandeRecrutement) => {
            console.log(response);
            this.demandeDetails = response;
            this.currentDemande = response;
            this.demandeurAvis = response?.demandeurAvis?.nom + ' ' + response?.demandeurAvis?.prenom;
            this.currentStatus = response.statut;
            this.isReadOnly = !this.canEdit();
            this.cdr.detectChanges();
          }
        });
    } else if (currentDemande.type === DemandeType.Renouvellement) {
      this.demandeRenouvellementService
        .getDemandesRenouvellementById(currentDemande.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response: Renouvellement) => {
            this.demandeRenouvellement = response;
            console.log('demandeRenouvellement', this.demandeRenouvellement);
            this.demandeurAvis = response?.demandeurAvis?.nom + ' ' + response?.demandeurAvis?.prenom;
            this.currentDemande = response;
            this.currentStatus = response.statut;
            this.isReadOnly = !this.canEdit();
            this.cdr.detectChanges();
          }
        });
    }
  }

  canEdit(): boolean {
    return (this.currentStatus === StatusAvis.A_VALIDER || this.currentStatus === StatusAvis.A_VALIDER_N0) && this.data.action === 'edit';
  }

  canShowReject(): boolean {
    const statut = (this.currentDemande as any)?.statut;
    return statut ? this.demandeStatusService.showReject(statut) : false;
  }

  OnToggleChange(isChecked: boolean): void {
    this.isOpened = isChecked;
  }

  ngAfterViewInit(): void {
    // Listen to sidenav open/close events to update PDF viewer size
    this.sidenav.openedChange.pipe(takeUntil(this.destroy$)).subscribe((isOpen) => {
      if (this.pdfViewer?.updateSize && this.pdfSource) {
        if (!isOpen) {
          // Store current zoom before setting to 1
          this.previousZoom = this.pmdfZoomFactor;
          this.pmdfZoomFactor = 1;
        } else {
          // Restore previous zoom
          this.pmdfZoomFactor = this.previousZoom;
        }
        this.pdfViewer.updateSize();
        this.cdr.detectChanges();
      }
    });
  }

  loadCurrentPdf(): void {
    if (!this.data.demandes?.length) return;
    const currentDemande = this.data.demandes[this.currentPdfIndex];
    this.fileService
      .getDemandePdf(currentDemande.id, currentDemande.type)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: Blob) => {
          this.pdfSource = URL.createObjectURL(response);
          this.cdr.detectChanges();
        }
      });
  }

  onValidate(): void {
    if (!this.data.demandes?.length) return;
    const currentDemande = this.data.demandes[this.currentPdfIndex];

    const motifRefus =
      this.currentDemandeType === DemandeType.Recrutement
        ? this.recrutementForm?.motifRefusControl.value
        : this.renouvellementForm?.motifRefusControl.value;
    // on supprime les espaces
    if (motifRefus && !motifRefus?.trim()) {
      return;
    }
    this.validationDemandesService
      .validerDemande(currentDemande.id, currentDemande.type, motifRefus ?? '')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // Show success modal
          const successModal = this.matDialog.open(ValidationModalComponent, {
            width: '500px',
            panelClass: 'success-dialog',
            data: {
              title: 'Confirmation',
              content: `La demande de ${currentDemande.type === DemandeType.Recrutement ? 'recrutement' : 'renouvellement'} a été validée avec succès.`
            }
          });

          // Refresh details immediately
          this.loadDemandeDetails();
          this.needsRefresh = true;

          successModal
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
              this.loadCurrentPdf();
              this.loadDemandeDetails();
              // Reset motif refus after successful validation
              if (this.currentDemandeType === DemandeType.Recrutement) {
                this.recrutementForm?.motifRefusControl.reset();
              } else {
                this.renouvellementForm?.motifRefusControl.reset();
              }
              this.cdr.detectChanges();
            });
        },
        error: (error: unknown) => {
          console.error('Error validating demand:', error);
          this.matDialog.open(ValidationModalComponent, {
            width: '500px',
            panelClass: 'error-dialog',
            data: {
              title: 'Erreur',
              content: 'Une erreur est survenue lors de la validation de la demande',
              icon: 'error'
            }
          });
        }
      });
  }

  onRefuse(): void {
    if (!this.data.demandes?.length) return;
    const currentDemande = this.data.demandes[this.currentPdfIndex];
    if (!currentDemande) return;

    const motifRefus =
      this.currentDemandeType === DemandeType.Recrutement
        ? this.recrutementForm?.motifRefusControl.value
        : this.renouvellementForm?.motifRefusControl.value;

    if (!motifRefus?.trim()) {
      this.matDialog.open(ValidationModalComponent, {
        width: '500px',
        panelClass: 'warning-dialog',
        data: {
          title: 'Attention',
          content: 'Veuillez saisir un motif de refus',
          icon: 'warning'
        }
      });
      return;
    }

    this.validationDemandesService
      .refuserDemande(currentDemande.id, currentDemande.type, motifRefus)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // Show success modal
          const successModal = this.matDialog.open(ValidationModalComponent, {
            width: '500px',
            panelClass: 'success-dialog',
            data: {
              title: 'Confirmation',
              content: `La demande de ${currentDemande.type === DemandeType.Recrutement ? 'recrutement' : 'renouvellement'} a été refusée avec succès.`
            }
          });

          // Refresh details immediately
          this.loadDemandeDetails();
          this.needsRefresh = true;

          successModal
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
              this.loadCurrentPdf();
              this.loadDemandeDetails();
              this.cdr.detectChanges();
            });
        },
        error: (error: unknown) => {
          console.error('Error refusing demand:', error);
          this.matDialog.open(ValidationModalComponent, {
            width: '500px',
            panelClass: 'error-dialog',
            data: {
              title: 'Erreur',
              content: 'Une erreur est survenue lors du refus de la demande',
              icon: 'error'
            }
          });
        }
      });
  }

  shouldShowRecallValidationButton(): boolean {
    if (this.data.action === 'view' || this.data.action === 'edit' || this.data.action === 'advice') return false;
    if (!this.currentDemande?.statut) return false;
    if (!this.hasRecallPermission('validation')) return false;
    return this.currentDemande.statut.includes('VALIDEES');
  }

  shouldShowRecallRefuseButton(): boolean {
    if (this.data.action === 'view' || this.data.action === 'edit' || this.data.action === 'advice') return false;
    if (!this.currentDemande?.statut) return false;
    if (!this.hasRecallPermission('refus')) return false;
    return this.currentDemande.statut.includes('REFUSEES');
  }

  onRecallValidation(): void {
    this.handleRecall('validation');
  }

  onRecallRefuse(): void {
    this.handleRecall('refus');
  }

  private applyRecall(
    demandes: Array<{ id: string; type: DemandeType }>,
    motif: string,
    type: 'validation' | 'refus'
  ): void {
    const cible = demandes[0];
    const payload = [
      {
        id: cible.id,
        typeDemande: cible.type,
        motif
      }
    ];

    this.validationDemandesService
      .rappelerDemandes(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.needsRefresh = true;
          this.loadCurrentPdf();
          this.loadDemandeDetails();
          const successContent =
            type === 'validation'
              ? demandes.length > 1
                ? 'Les validations sélectionnées ont été annulées avec succès.'
                : 'La validation a été annulée avec succès.'
              : demandes.length > 1
                ? 'Les refus des demandes ont été annulés avec succès.'
                : 'Le refus de la demande a été annulée avec succès.';
          this.matDialog.open(ValidationModalComponent, {
            width: '500px',
            panelClass: 'success-dialog',
            data: {
              title: 'Confirmation',
              content: successContent
            }
          });
        },
        error: (error: unknown) => {
          console.error('Error recalling demand:', error);
          this.matDialog.open(ValidationModalComponent, {
            width: '500px',
            panelClass: 'error-dialog',
            data: {
              title: 'Erreur',
              content: "Une erreur est survenue lors de l'annulation.",
              icon: 'error'
            }
          });
        }
      });
  }

  private handleRecall(type: 'validation' | 'refus'): void {
    const demandes = this.getDemandesForScope();
    if (!demandes.length) return;

    const description = `Vous êtes sur le point d'annuler ${type === 'validation' ? 'la validation' : 'le refus'
      } de cette demande. Veuillez saisir un motif.`;

    this.matDialog
      .open(RecallActionDialogComponent, {
        width: '540px',
        data: {
          title: type === 'validation' ? 'Annuler la validation' : 'Annuler le refus',
          description
        }
      })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((motif?: string) => {
        if (!motif?.trim()) return;
        this.applyRecall(demandes, motif.trim(), type);
      });
  }

  private hasRecallPermission(_type: 'validation' | 'refus'): boolean {
    if (!this.isAdmin) return false;

    const statut = this.currentStatus ?? this.currentDemande?.statut;
    if (!statut) return false;

    const isPending = statut.includes(StatusAvis.A_VALIDER);
    const isArchived = statut === StatusAvis.ARCHIVEES;

    return !isPending && !isArchived;
  }

  private getDemandesForScope() {
    if (!this.data.demandes?.length) {
      return [];
    }
    return [this.data.demandes[this.currentPdfIndex]];
  }

  onPrint(): void {
    if (this.pdfSource) {
      printJS({
        printable: this.pdfSource,
        type: 'pdf',
        base64: false,
        showModal: true
      });
    }
  }

  onDownload(): void {
    if (this.pdfSource && typeof this.pdfSource === 'string') {
      fetch(this.pdfSource)
        .then((response) => {
          if (!response.ok) {
            throw new Error('Failed to fetch PDF file');
          }
          return response.blob();
        })
        .then((blob) => {
          const filename = `${this.data?.filename || 'download'}_${new Date().toISOString().slice(0, 10)}.pdf`;
          saveAs(blob, filename);
        })
        .catch((error) => {
          console.error('Error downloading the file:', error);
          alert('Error downloading the PDF file.');
        });
    } else {
      alert('Invalid PDF source.');
    }
  }

  onClose(): void {
    this.dialog.close(this.needsRefresh ? 'refresh' : false);
  }

  onSave(): void {
    if (!this.currentDemande) return;

    if (this.currentDemandeType === DemandeType.Recrutement) {
      // Get the recrutementForm instance
      const recrutementFormComponent = this.recrutementForm;
      if (!recrutementFormComponent) return;

      // Get the main form and validate it using the utility service
      const isValid = recrutementFormComponent.recrutementForm.valid;
      console.log(recrutementFormComponent.recrutementForm);

      if (!isValid) {
        const invalidControls = recrutementFormComponent.getInvalidControls();
        console.error('Form is invalid. Invalid controls:', invalidControls);
        // Show validation error dialog
        this.matDialog.open(ValidationModalComponent, {
          width: '500px',
          panelClass: 'warning-dialog',
          data: {
            title: 'Formulaire incomplet',
            content: 'Veuillez remplir tous les champs obligatoires avant de sauvegarder.',
            icon: 'warning'
          }
        });
        return;
      }

      // Get form values and construct payload
      const formValues = recrutementFormComponent.recrutementForm.getRawValue();
      const payload = this.prepareRecrutementRequestBody(formValues);

      // Save the demande
      this.saveRecrutementDemande(payload);
    } else if (this.currentDemandeType === DemandeType.Renouvellement) {
      if (!this.renouvellementForm) return;
      if (!this.renouvellementForm.renouvellementForm.valid) {
        this.matDialog.open(ValidationModalComponent, {
          width: '500px',
          panelClass: 'warning-dialog',
          data: {
            title: 'Formulaire incomplet',
            content: 'Veuillez remplir tous les champs obligatoires avant de sauvegarder.',
            icon: 'warning'
          }
        });
        return;
      }

      this.saveRenouvellementDemande();
    }
  }

  private prepareRecrutementRequestBody(formValues: DemandeRecrutementFormValue): ModifyRecruitmentRequest {
    // Determine type of recruitment
    let typeRecrutementSouhaite = 'Interne';
    if (formValues.caracteristiquesData?.interne && formValues.caracteristiquesData?.externe) {
      typeRecrutementSouhaite = 'Interne_Externe';
    } else if (formValues.caracteristiquesData?.externe) {
      typeRecrutementSouhaite = 'Externe';
    }

    // Get posteBudgete as boolean
    const posteBudgete = formValues.caracteristiquesData?.posteBudgete ?? undefined;

    // Handle detenteurPoste
    const formDetenteurValue = formValues.caracteristiquesData?.detenteurPoste;
    let detenteurPosteObject: BaseUser | undefined;
    let detenteurNomValue: string | undefined;

    if (typeof formDetenteurValue === 'string') {
      detenteurNomValue = formDetenteurValue;
      detenteurPosteObject = undefined;
    } else if (formDetenteurValue && typeof formDetenteurValue === 'object' && 'id' in formDetenteurValue) {
      detenteurPosteObject = formDetenteurValue as BaseUser;
      detenteurNomValue = [formDetenteurValue.nom, formDetenteurValue.prenom].filter(Boolean).join(' ') || undefined;
    }

    // Cast to DemandeRecrutement since this method is only called for recruitment requests
    const recruitmentDemande = this.currentDemande as DemandeRecrutement;

    const payload: ModifyRecruitmentRequest = {
      id: recruitmentDemande?.id!,

      // Information data
      numPoste: formValues.informationsData?.numPoste?.code ? +formValues.informationsData.numPoste.code : undefined,
      typeRecrutement: formValues.informationsData?.typeRecrutement || undefined,
      intitulePoste: formValues.informationsData?.intitulePoste || undefined,
      serviceFonctionnel: formValues.informationsData?.service || undefined,
      motifLiberationPoste: formValues.informationsData?.motifLiberation || undefined,

      // Caracteristiques data
      posteBudgete: posteBudgete,
      etatPoste: formValues.caracteristiquesData?.etatPoste || undefined,
      dateRecrSouhaite: this.formatDate(formValues.caracteristiquesData?.dateRecrutement) || undefined,
      dateDepartDefinitif: this.formatDate(formValues.caracteristiquesData?.dateDepart) || undefined,
      typeRecrutementSouhaite: typeRecrutementSouhaite,
      occupationPoste: formValues.caracteristiquesData?.occupationPoste || recruitmentDemande?.occupationPoste,
      detenteurPoste: detenteurPosteObject,
      detenteurNom: detenteurNomValue,
      categoriePoste: formValues.caracteristiquesData?.categoriePoste || recruitmentDemande?.categoriePoste,

      // Motivation data
      motivationDemande: formValues.motivationDemande || undefined,

      // Organizational structure
      pole: recruitmentDemande?.pole,
      direction: recruitmentDemande?.direction,

      // Destinataires data
      destinataireValidation: this.getDestinataireAsUser(formValues.destinatairesData?.destinataireValidation),
      destinataireCharge: this.getDestinataireAsUser(formValues.destinatairesData?.destinataireChargeGpec),
      destinataireDRH: this.getDestinataireAsUser(formValues.destinatairesData?.destinataireDrh),
      destinataireDG: this.getDestinataireAsUser(formValues.destinatairesData?.destinataireDg),
      destinataireResponsable: this.getDestinataireAsUser(formValues.destinatairesData?.destinataireResponsable),
      destinataireDgs: this.getDestinataireAsUser(formValues.destinatairesData?.destinataireDgs),
      destinataireArchivage: this.getDestinataireAsUser(recruitmentDemande?.destinataireArchivage),

      // Status and workflow
      statut: recruitmentDemande?.statut ?? StatusAvis.A_VALIDER,
      commentaires: recruitmentDemande?.commentaires,

      // Avis management
      destinataireAvis: this.getDestinataireAsUser(recruitmentDemande?.destinataireAvis),
      demandeAvisDate: recruitmentDemande?.demandeAvisDate,
      demandeAvisMotif: recruitmentDemande?.demandeAvisMotif,
      demandeurAvis: this.getDestinataireAsUser(recruitmentDemande?.demandeurAvis),
      flagDemandeAvis: recruitmentDemande?.flagDemandeAvis,

      // Document management
      generatedPDF: recruitmentDemande?.generatedPDF,

      // Audit fields that are allowed
      emetteur: this.getDestinataireAsUser(recruitmentDemande?.emetteur)
    };

    return payload;
  }

  private saveRecrutementDemande(payload: ModifyRecruitmentRequest): void {
    this.demandeRecrutementService
      .modifierDemandesRecrutement(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.handleSaveSuccess(),
        error: (error: unknown) => this.handleSaveError(error)
      });
  }

  private saveRenouvellementDemande(): void {
    // Type guard to ensure currentDemande exists and has a valid id
    if (!this.currentDemande?.id) {
      this.handleSaveError(new Error('Invalid demande: missing id'));
      return;
    }

    const requestBody = this.prepareRenouvellementRequestBody();
    this.demandeRenouvellementService
      .updateDemandesRenouvellement(requestBody, this.currentDemande?.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.handleSaveSuccess(),
        error: (error: unknown) => this.handleSaveError(error)
      });
  }

  private handleSaveSuccess(): void {
    this.matDialog.open(ValidationModalComponent, {
      width: '500px',
      panelClass: 'success-dialog',
      data: {
        title: 'Confirmation',
        content: 'La demande a été enregistrée avec succès.'
      }
    });
    this.needsRefresh = true;

    // Refresh the current demande and PDF
    this.loadDemandeDetails();
    this.loadCurrentPdf();
  }

  private handleSaveError(error: unknown): void {
    this.matDialog.open(ValidationModalComponent, {
      width: '500px',
      panelClass: 'error-dialog',
      data: {
        title: 'Erreur',
        content: "Une erreur est survenue lors de l'enregistrement de la demande",
        icon: 'error'
      }
    });
  }

  private prepareRenouvellementRequestBody(): DemandeRenouvellementPost {
    if (!this.renouvellementForm) {
      throw new Error('Renouvellement form is not initialized');
    }

    // Access the form group inside the component
    const formValue = this.renouvellementForm.renouvellementForm.getRawValue();
    const situationAdministrativeData = formValue.situationAdministrativeData;
    const informationsRenouvellementData = formValue.informationsRenouvellementData;
    const pertinenceExistenceData = formValue.pertinenceExistenceData;
    const destinatairesData = formValue.destinatairesData;

    // Extract values from the form
    return {
      id: this.demandeRenouvellement?.id,
      agent: { id: situationAdministrativeData?.agent?.id ?? '' },
      typeRenouvellement: {
        id: this.extractIdFromTypeRenouvellement(situationAdministrativeData?.typeRenouvellement)
      },
      tauxEmploi: situationAdministrativeData?.tauxEmploi ?? null,
      dateEntree: this.formatDate(situationAdministrativeData?.dateEntree),
      posteOccupe: { id: situationAdministrativeData?.posteOccupe?.id ?? '' },
      typeContrat: { id: situationAdministrativeData?.typeContrat?.id ?? '' },
      pole: { id: situationAdministrativeData?.pole?.id ?? '' },
      direction: { id: situationAdministrativeData?.direction?.id ?? '' },
      serviceFonctionnel: { id: situationAdministrativeData?.serviceFonctionnel?.id ?? '' },
      agentRemplace: situationAdministrativeData?.agentRemplace ? 'true' : 'false',
      dateDebutContrat: this.formatDate(situationAdministrativeData?.dateRange?.start),
      dateFinContrat: this.formatDate(situationAdministrativeData?.dateRange?.end),

      // Information renouvellement data
      contratMemePoste: informationsRenouvellementData?.motif === 'contratMemePoste' ? 'true' : 'false',
      contratConditionsDiffs: informationsRenouvellementData?.motif === 'contratConditionsDiffs' ? 'true' : 'false',
      refusContratDemande: informationsRenouvellementData?.motif === 'refusContratDemande' ? 'true' : 'false',
      refusContratConditions: informationsRenouvellementData?.motif === 'refusContratConditions' ? 'true' : 'false',

      // Pertinence existence data
      pertinenceMission: pertinenceExistenceData?.pertinenceMission ?? null,
      renouvellementJustifie: pertinenceExistenceData?.renouvellementAJustifier ?? null,

      // Status and destinataires
      statut: this.demandeRenouvellement?.statut ?? StatusAvis.A_VALIDER,
      destinataireValidation: this.getDestinataireId(destinatairesData?.destinataireValidation),
      destinataireCharge: this.getDestinataireId(destinatairesData?.destinataireChargeGpec),
      destinataireDG: this.getDestinataireId(destinatairesData?.destinataireDg),
      destinataireDRH: this.getDestinataireId(destinatairesData?.destinataireDrh),
      destinataireResponsable: this.getDestinataireId(destinatairesData?.destinataireResponsable),
      destinataireDgs: this.getDestinataireId(destinatairesData?.destinataireDgs)
    };
  }

  private formatDate(date: Date | string | null | undefined): string | null {
    if (!date) return null;

    // Create a proper Luxon DateTime object from the date using fromJSDate
    const jsDate = new Date(date);
    const luxonDate = DateTime.fromJSDate(jsDate);
    return luxonDate.toFormat('yyyy-MM-dd');
  }

  private getDestinataireId(destinataire: BaseUser | null | undefined): { id: string } | null {
    return destinataire?.id ? { id: destinataire.id } : null;
  }

  private getDestinataireAsUser(destinataire: BaseUser | null | undefined): BaseUser | undefined {
    return destinataire || undefined;
  }

  private extractIdFromTypeRenouvellement(typeRenouvellement: unknown): string {
    // Handle null/undefined case
    if (!typeRenouvellement) {
      return '';
    }

    // If it's already a string, return it
    if (typeof typeRenouvellement === 'string') {
      return typeRenouvellement;
    }

    // If it's an object with an id property, extract the id
    if (typeof typeRenouvellement === 'object' && typeRenouvellement !== null) {
      const obj = typeRenouvellement as { id?: string };
      return obj.id ?? '';
    }

    // Fallback case
    return '';
  }

  onDelete(): void {
    if (this.currentDemande) {
      const confirmDialog = this.matDialog.open(ValidationModalComponent, {
        width: '500px',
        panelClass: 'warning-dialog',
        data: {
          title: 'Confirmation',
          content: 'Êtes-vous sûr de vouloir supprimer cette demande ?',
          icon: 'warning',
          showCancelButton: true
        }
      });

      confirmDialog
        .afterClosed()
        .pipe(takeUntil(this.destroy$))
        .subscribe((result) => {
          if (result === true) {
            if (this.currentDemandeType === DemandeType.Recrutement) {
              this.demandeRecrutementService
                .deleteDemandesRecrutement(this.currentDemande?.id ?? '')
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                  next: () => {
                    this.handleSuccessfulDeletion();
                  },
                  error: (error: unknown) => {
                    this.handleDeletionError();
                  }
                });
            } else if (this.currentDemandeType === DemandeType.Renouvellement) {
              this.demandeRenouvellementService
                .deleteDemandesRenouvellement(this.currentDemande?.id ?? '')
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                  next: () => {
                    this.handleSuccessfulDeletion();
                  },
                  error: (error: unknown) => {
                    this.handleDeletionError();
                  }
                });
            }
          }
        });
    }
  }

  private handleSuccessfulDeletion() {
    this.needsRefresh = true;
    // Show success message
    this.matDialog.open(ValidationModalComponent, {
      width: '500px',
      panelClass: 'success-dialog',
      data: {
        title: 'Confirmation',
        content: 'La demande a été supprimée avec succès.'
      }
    });

    // Remove the current demande from the array
    if (this.data.demandes?.length) {
      this.data.demandes.splice(this.currentPdfIndex, 1);

      // Check if there are any demandes left
      if (this.data.demandes.length === 0) {
        // No demandes left, close the dialog
        this.onClose();
      } else {
        // Adjust current index if needed
        if (this.currentPdfIndex >= this.data.demandes.length) {
          this.currentPdfIndex = this.data.demandes.length - 1;
        }
        // Load the next/previous demande
        this.loadCurrentPdf();
        this.loadDemandeDetails();
      }
    } else {
      this.onClose();
    }
  }

  private handleDeletionError() {
    this.matDialog.open(ValidationModalComponent, {
      width: '500px',
      panelClass: 'error-dialog',
      data: {
        title: 'Erreur',
        content: 'Une erreur est survenue lors de la suppression de la demande',
        icon: 'error'
      }
    });
  }

  onZoomIn() {
    this.pmdfZoomFactor = this.pmdfZoomFactor + 0.1;
  }

  onZoomOut() {
    this.pmdfZoomFactor = this.pmdfZoomFactor - 0.1;
  }

  onNextPdf() {
    if (!this.data.demandes?.length) return;
    if (this.currentPdfIndex < this.data.demandes.length - 1) {
      this.currentPdfIndex++;
      this.loadCurrentPdf();
      this.loadDemandeDetails();
    }
  }

  onPreviousPdf() {
    if (this.currentPdfIndex > 0) {
      this.currentPdfIndex--;
      this.loadCurrentPdf();
      this.loadDemandeDetails();
    }
  }

  replierPanels() {
    if (this.currentDemandeType === DemandeType.Recrutement && this.recrutementForm?.allpanels) {
      this.recrutementForm.allpanels.forEach((panel: MatExpansionPanel) => panel.close());
    } else if (this.currentDemandeType === DemandeType.Renouvellement && this.renouvellementForm?.allpanels) {
      this.renouvellementForm.allpanels.forEach((panel) => panel.close());
    }
  }

  deplierPanels() {
    if (this.currentDemandeType === DemandeType.Recrutement && this.recrutementForm?.allpanels) {
      this.recrutementForm.allpanels.forEach((panel: MatExpansionPanel) => panel.open());
    } else if (this.currentDemandeType === DemandeType.Renouvellement && this.renouvellementForm?.allpanels) {
      this.renouvellementForm.allpanels.forEach((panel) => panel.open());
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getTitle(): string {
    const demandeType = this.currentDemandeType === DemandeType.Recrutement ? 'recrutement' : 'renouvellement';

    switch (this.data.action) {
      case 'validate':
      case 'refuse':
      case 'review':
        return `Validation demande de ${demandeType}`;
      case 'edit':
        return `Modification demande de ${demandeType}`;
      case 'view':
      default:
        return `Consultation demande de ${demandeType}`;
    }
  }

  getAdvices() {
    this.matDialog
      .open(GetAdviceComponent, {
        width: '600px'
      })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: null | { destinataireId: string; note: string }) => {
        if (!result) {
          return;
        }
        const requestBody: CreateDemandeAvisRequest = {
          [this.currentDemandeType]: [
            { idDemande: this.currentDemande?.id ?? '', idAgent: result.destinataireId, motif: result.note, typeDemande: this.currentDemandeType }
          ]
        };
        this.demandesService
          .createAdvice(requestBody)
          .pipe(take(1))
          .subscribe((advice) => {
            this.needsRefresh = true;
            this.loadCurrentPdf();
            this.loadDemandeDetails();
            this.matDialog.open(ValidationModalComponent, {
              width: '500px',
              panelClass: 'success-dialog',
              data: {
                title: 'Confirmation',
                content: "La demande d'avis a été créée avec succès."
              }
            });
          });
      });
  }
  answerAdvice() {
    this.matDialog
      .open(GetAdviceComponent, {
        width: '600px',
        data: {
          destinataireId: (this.currentDemande as any)?.destinataireAvis?.id ?? ''
        }
      })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: null | { destinataireId: string; note: string; files: File[] }) => {
        if (!result) {
          return;
        }
        const requestBody: AnswerDemandeAvisRequest = {
          demandeAvisReponse: {
            idDemande: this.currentDemande?.id ?? '',
            typeDemande: this.currentDemandeType,
            reponse: result.note,
            privee: false
          },
          piecesJointes: result.files
        };
        this.demandesService.answerDemandeAvis(this.currentDemande?.id ?? '', requestBody).subscribe((advice) => {
          this.needsRefresh = true;
          this.loadCurrentPdf();
          this.loadDemandeDetails();
          this.matDialog.open(ValidationModalComponent, {
            width: '500px',
            panelClass: 'success-dialog',
            data: {
              title: 'Confirmation',
              content: "La demande d'avis a été répondu avec succès."
            }
          });
        });
      });
  }
  cancelAdvice() {
    this.demandesService
      .cancelDemandeAvis(this.currentDemande?.id ?? '', this.currentDemandeType)
      .pipe(take(1))
      .subscribe((advice) => {
        this.needsRefresh = true;
        this.loadCurrentPdf();
        this.loadDemandeDetails();
        this.matDialog.open(ValidationModalComponent, {
          width: '500px',
          panelClass: 'success-dialog',
          data: {
            title: 'Confirmation',
            content: "La demande d'avis a été annulée avec succès."
          }
        });
      });
  }

  getValidateButtonText(): string {
    return this.currentStatus === StatusAvis.VALIDEES_N5 ? 'Archiver la demande' : 'Valider la demande';
  }

  shouldShowValidateButton(): boolean {
    if (!this.currentDemande?.statut) return false;
    if (this.currentDemande.statut.includes('REFUSEES')) return false;
    if (this.currentDemande.statut === StatusAvis.ARCHIVEES) return false;
    if (this.data.action === 'refuse') return false;
    if (!this.currentDemande?.canValidate) return false;

    return true;
  }

  shouldShowAskAdviceButton(): boolean {
    if (!this.currentDemande?.statut) return false;
    if (this.currentDemande.statut.includes('REFUSEES')) return false;
    if (this.currentDemande.statut === StatusAvis.ARCHIVEES) return false;
    if (this.data.action !== 'validate' && this.data.action !== 'refuse' && this.data.action !== 'review') return false;
    if (!this.currentDemande?.canValidate) return false;
    if (this.currentDemande?.flagDemandeAvis === true) return false;

    return true;
  }

  private extractDetenteurNom(detenteurVal: any): string | null {
    if (typeof detenteurVal === 'string') {
      return detenteurVal || null;
    }

    if (!detenteurVal || typeof detenteurVal !== 'object') {
      return null;
    }

    // Try detenteurNom first
    if (detenteurVal.detenteurNom?.trim()) {
      return detenteurVal.detenteurNom.trim();
    }

    // Fallback to building from nom + prenom
    const nom = detenteurVal.nom?.trim();
    const prenom = detenteurVal.prenom?.trim();

    if (!nom && !prenom) {
      return null;
    }

    return [nom, prenom].filter(Boolean).join(' ');
  }
}
